import { Alert } from "components/elements";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import * as service from "../service/StpTimeSheetApi";
import { setEmployeePaginationByRole, setEmployeeRoleMappingPagination, setIsLoading, setProjectWorkflow, setRoleList, setTaskList, setTaskPagination } from "../slices/TimeSheetSlice";
import useCreateSettingProject from "./useCreateSettingProject";
import useSettingTimeSheetProject from "./useSettingTimeSheetProject";
import { Card, Divider, Dropdown, Flex, Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import { DeleteOutlined, EditOutlined, PlusCircleOutlined, RightOutlined } from "@ant-design/icons";
import { generateRandomString } from "utils/helper";
import { getRoleOptions } from "features/masterData/services/optionsApi";
import { setRolePermission } from "features/masterData/slices/optionsSlice";
import FormAntd from "components/form/FormProvide/FormAntd";
import { useForm } from "antd/es/form/Form";

const TAB_KEY = {
    PROJECT_INFO: "project_info",
    ALL_WORK: "all_work",
    ROLE: "role",
    WORKFLOW: "workFlow"
}
const useEditSettingProject = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch()
    const { id } = useParams()
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
    const [isRoleEditModalOpen, setIsRoleEditModalOpen] = useState(false);
    const [paginationTask, setPaginationTask] = useState({ page: 0, size: 10 })
    const [paginationEmployeeMapping, setPaginationEmployeeMapping] = useState({ page: 0, size: 10 })
    const [paginationEmployeeByRole, setPaginationEmployeeByRole] = useState({ page: 0, size: 10 })
    const [taskOnEdit, setTaskOnEdit] = useState()
    const [roleOnEdit, setRoleOnEdit] = useState()
    const [employeeSelected, setEmployeeSelected] = useState()
    const [roleAction, setRoleAction] = useState("VIEW")
    const [searchTxt, setSearchTxt] = useState("")

    const { isLoading,
        project,
        taskList,
        roleList,
        taskPagination,
        employeeRoleMappingPagination,
        employeePaginationByRole,
        projectWorkflow,
    } = useSelector((state) => state.timeSheet)
    const { currentCompany, currentEmployee } = useSelector((state) => state.company)
    const { onSubmit, employeeOptions, getDropdownEmployee, PROJECT_STATUS, employeeList } = useCreateSettingProject()
    const { getStpTimeSheetById } = useSettingTimeSheetProject();

    const [list, setList] = useState([{ type: '1' }])
    const [listworkflow, setListWorkFlow] = useState([])
    const [selectedRoleId, setSelectedRoleId] = useState(null);

    const location = useLocation();
    const isView = location.pathname.includes("/view/");

    const taskOption = useMemo(() => {
        return taskList?.map((o) => {
            return {
                value: o?.id,
                label: i18n.language === "en" ? o?.taskNameEn : o?.taskName
            }
        })
    }, [taskList, i18n.language])

    const roleOption = useMemo(() => {
        return roleList?.map((o) => {
            return {
                value: o?.stpTimeSheetRoleId ?? o?.id,
                label: i18n.language === "en" ? o?.roleNameEn : o?.roleName
            }
        })
    }, [roleList, i18n.language])

    const tableDataSourceCreate = useMemo(() => {
        return employeeList?.filter((o) => employeeSelected?.includes(o?.id))?.sort((a, b) => a?.employeeNo?.localeCompare(b?.employeeNo));
    }, [employeeList, employeeSelected])

    const toParams = useCallback((params) => {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            ...params,
            page: pagination.page || 0,
            size: pagination.pageSize || 10,
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;

    }, [])

    const onAddEmployee = ((record) => {
        setEmployeeSelected(record)
    })

    const onRemoveEmployeeRoleCreate = useCallback((record) => {
        let employee = employeeSelected?.filter((o) => o != record?.id)
        setEmployeeSelected(employee)
    }, [employeeSelected])

    const fetchDropdownRoleByProject = useCallback(async () => {
        dispatch(setIsLoading(true))
        await service.dropdownRoleByProject({
            companyId: currentCompany,
            employeeId: currentEmployee,
            stpTimeSheetId: id
        }).then((response) => {
            dispatch(setRoleList(response?.data?.content?.sort((a, b) => a?.roleName?.localeCompare(b?.roleName))))
        }).catch((error) => {
            Alert({ type: "error", resultObject: error })
        }).finally(() => {
            dispatch(setIsLoading(false))
        })
    }, [currentCompany, currentEmployee])

    const fetchDropdownMstRole = useCallback(async () => {
        dispatch(setIsLoading(true))
        await service.dropdownTimeSheetRole({
            companyId: currentCompany,
            employeeId: currentEmployee,
            stpTimeSheetId: id
        }).then((response) => {
            dispatch(setRoleList(response?.data?.content?.sort((a, b) => a?.roleName?.localeCompare(b?.roleName))))
        }).catch((error) => {
            Alert({ type: "error", resultObject: error })
        }).finally(() => {
            dispatch(setIsLoading(false))
        })
    }, [currentCompany, currentEmployee])

    const fetchCompanyDropdownRole = useCallback(async () => {
        try {
            dispatch(setIsLoading(true));
            const response = await service.dropdownCompanyRole({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });

            dispatch(setRoleList(response?.data || []));
        } catch (error) {
            alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    const fetchDropdownTask = useCallback(async (params) => {
        dispatch(setIsLoading(true))
        await service.dropdownTask({
            companyId: currentCompany,
            employeeId: currentEmployee,
            stpTimeSheetId: id,
            roleId: params?.role?.roleId
        }).then((response) => {
            dispatch(setTaskList(response?.data?.content?.sort((a, b) => a?.taskName?.localeCompare(b?.taskName))))
        }).catch((error) => {
            Alert({ type: "error", resultObject: error })
        }).finally(() => {
            dispatch(setIsLoading(false))
        })
    }, [currentCompany, currentEmployee])

    const fetchtTaskPagination = useCallback(async (pamar) => {
        dispatch(setIsLoading(true))
        await service.taskGroupByRoleList({
            ...pamar,
            companyId: currentCompany,
            employeeId: currentEmployee,
            stpTimeSheetId: id
        }).then((response) => {
            dispatch(setTaskPagination(response?.data?.content?.sort((a, b) => a?.role?.roleName?.localeCompare(b?.role?.roleName))))
            // setPaginationTask({
            //     ...pamar?.pagination,
            //     current: response?.data?.page?.number + 1,
            //     pageNo: response?.data?.page?.number + 1,
            //     page: response?.data?.page?.number + 1,
            //     total: response.data?.page?.totalElements,
            //     size: response?.data?.page?.size
            // })

        }).catch((error) => {
            Alert({ type: "error", resultObject: error })
        }).finally(() => {
            dispatch(setIsLoading(false))
        })
    }, [currentCompany, currentEmployee])

    const fetchtEmployeeRoleMappingPagination = useCallback(async (pamar) => {
        dispatch(setIsLoading(true))
        await service.getEmployeeRoleMappingPagination({
            ...pamar,
            companyId: currentCompany,
            employeeId: currentEmployee,
            stpTimeSheetId: id
        }).then((response) => {
            dispatch(setEmployeeRoleMappingPagination(response?.data?.page?.content))
            setPaginationEmployeeMapping({
                ...pamar?.pagination,
                current: response?.data?.page?.number + 1,
                pageNo: response?.data?.page?.number + 1,
                page: response?.data?.page?.number + 1,
                total: response.data?.page?.totalElements,
                size: response?.data?.page?.size
            })
        }).catch((error) => {
            Alert({ type: "error", resultObject: error })
        }).finally(() => {
            dispatch(setIsLoading(false))
        })
    }, [currentCompany, currentEmployee])

    const fetchtEmployeePaginationByRoleIdAndTimeSheetId = useCallback(async (pamar) => {
        dispatch(setIsLoading(true))
        await service.getEmployeePaginationByRoleIdAndTimeSheetId({
            ...pamar,
            companyId: currentCompany,
            employeeId: currentEmployee,
            stpTimeSheetId: id,
            searchTxt: searchTxt ? searchTxt : ""
        }).then((response) => {
            dispatch(setEmployeePaginationByRole(response?.data?.page?.content))
            setPaginationEmployeeByRole({
                ...pamar?.pagination,
                current: response?.data?.page?.number + 1,
                pageNo: response?.data?.page?.number + 1,
                page: response?.data?.page?.number + 1,
                total: response.data?.page?.totalElements,
                size: response?.data?.page?.size
            })

        }).catch((error) => {
            Alert({ type: "error", resultObject: error })
        }).finally(() => {
            dispatch(setIsLoading(false))
        })
    }, [currentCompany, currentEmployee, searchTxt])

    const onSubmitTask = useCallback(async (params) => {

        dispatch(setIsLoading(true))
        await service.editStpTimeSheetTask({
            ...params,
            companyId: currentCompany,
            employeeId: currentEmployee,
            stpTimeSheetId: id,
            taskId: params?.id
        }).then((res) => {
            fetchtTaskPagination({
                page: paginationTask?.page - 1,
                size: paginationTask?.pageSize || 10,
                pageSize: paginationTask?.pageSize || 10,
                current: paginationTask.current - 1,
            })
            // fetchDropdownTask()
        }).finally(() => {
            setIsModalOpen(false)
            dispatch(setIsLoading(false))
        })
    }, [currentCompany, currentEmployee, id, paginationTask, fetchDropdownTask])

    const onSubmitMappingEmployeeRole = useCallback(async (params, form) => {
        dispatch(setIsLoading(true))
        await service.mappingEmployeeAndStpTimeSheetRole({
            companyId: currentCompany,
            employeeList: params?.employeeList?.length > 0 ? params?.employeeList : employeeSelected,
            stpTimeSheetId: id,
            stpTimeSheetRoleId: params?.stpTimeSheetRoleId,
            employeeId: currentEmployee
        }).then((res) => {
            fetchtEmployeePaginationByRoleIdAndTimeSheetId({
                page: paginationEmployeeByRole?.current - 1,
                size: paginationEmployeeByRole?.pageSize || 10,
                pageSize: paginationEmployeeByRole?.pageSize || 10,
                current: paginationEmployeeByRole.current - 1,
                stpTimeSheetRoleId: params?.stpTimeSheetRoleId,
            })
            fetchtEmployeeRoleMappingPagination({
                page: paginationEmployeeMapping?.current - 1,
                size: paginationEmployeeMapping?.pageSize || 10,
                pageSize: paginationEmployeeMapping?.pageSize || 10,
                current: paginationEmployeeMapping.current - 1,
                stpTimeSheetRoleId: params?.stpTimeSheetRoleId,
            })


        }).finally(() => {
            setIsRoleModalOpen(false)
            setEmployeeSelected()
            dispatch(setIsLoading(false))
            console.log("end finally");
        })
    }, [currentCompany, currentEmployee, id, paginationEmployeeMapping, paginationEmployeeByRole, employeeSelected, fetchtEmployeeRoleMappingPagination, fetchtEmployeePaginationByRoleIdAndTimeSheetId])

    const onRemoveEmployeeFromRole = useCallback(async (params) => {
        dispatch(setIsLoading(true))
        await service.removeEmployeeFromRole({
            companyId: currentCompany,
            employeeList: [params?.employeeId],
            stpTimeSheetId: id,
            stpTimeSheetRoleId: params?.stpTimeSheetRoleId,
            employeeId: currentEmployee
        }).then((res) => {
            fetchtEmployeePaginationByRoleIdAndTimeSheetId({
                page: paginationEmployeeByRole?.page - 1,
                size: paginationEmployeeByRole?.pageSize || 10,
                pageSize: paginationEmployeeByRole?.pageSize || 10,
                current: paginationEmployeeByRole.current - 1,
                stpTimeSheetRoleId: params?.stpTimeSheetRoleId,
            })
            fetchtEmployeeRoleMappingPagination({
                page: paginationEmployeeMapping?.current - 1,
                size: paginationEmployeeMapping?.pageSize || 10,
                pageSize: paginationEmployeeMapping?.pageSize || 10,
                current: paginationEmployeeMapping.current - 1,
                stpTimeSheetRoleId: params?.stpTimeSheetRoleId,
            })
        }).finally(() => {
            dispatch(setIsLoading(false))
        })
    }, [currentCompany, currentEmployee, id, paginationEmployeeByRole, employeeSelected, fetchtEmployeePaginationByRoleIdAndTimeSheetId, fetchtEmployeeRoleMappingPagination, paginationEmployeeMapping])

    const onEditTask = useCallback((params) => {
        setIsModalOpen(true)
        setTaskOnEdit(params)
        fetchDropdownTask(params)
    })

    const showModal = () => {
        setIsModalOpen(true);
    };

    const onCancel = () => {
        setIsModalOpen(false);
        setTaskOnEdit(null)
    };

    const showRoleModal = () => {
        setIsRoleModalOpen(true);
    };

    const onRoleModalCancel = () => {
        setIsRoleModalOpen(false);

        setEmployeeSelected()
    };

    const onRoleEditModalCancel = () => {
        setIsRoleEditModalOpen(false);
        setEmployeeSelected()
    };

    const onRoleChange = (value, params) => {
        setSelectedRoleId(value);
        fetchDropdownTask({ role: { roleId: value } });
    };

    const onViewRole = useCallback((record) => {
        setRoleAction("VIEW")
        setIsRoleEditModalOpen(true)
        setRoleOnEdit(record)
        fetchtEmployeePaginationByRoleIdAndTimeSheetId({ stpTimeSheetRoleId: record?.stpTimeSheetRoleId, page: 0, size: 10 })
    }, [fetchtEmployeePaginationByRoleIdAndTimeSheetId])

    const onEditRole = useCallback((record) => {
        setRoleAction("EDIT")
        setIsRoleEditModalOpen(true)
        setRoleOnEdit(record)
        fetchtEmployeePaginationByRoleIdAndTimeSheetId({ stpTimeSheetRoleId: record?.stpTimeSheetRoleId, page: 0, size: 10 })
    }, [fetchtEmployeePaginationByRoleIdAndTimeSheetId])

    const onSearchEmployeeByRole = useCallback((values) => {
        fetchtEmployeePaginationByRoleIdAndTimeSheetId({ stpTimeSheetRoleId: values?.stpTimeSheetRoleId, page: 0, size: 10 })
    }, [currentCompany, currentEmployee, fetchtEmployeePaginationByRoleIdAndTimeSheetId])

    const onTabChange = useCallback((key) => {
        setPaginationEmployeeMapping({ page: 0, size: 10 })
        setPaginationTask({ page: 0, size: 10 })

        switch (key) {
            case TAB_KEY.PROJECT_INFO:
                getStpTimeSheetById(id)
                break;
            case TAB_KEY.ALL_WORK:
                fetchtTaskPagination()
                // fetchDropdownTask()
                if (project?.forAll === "Y") {
                    fetchCompanyDropdownRole();
                }
                if (project?.forAll === "N") {
                    fetchDropdownRoleByProject();
                }
                break;
            case TAB_KEY.ROLE:
                fetchDropdownMstRole()
                fetchtEmployeeRoleMappingPagination({ page: 0, size: 10 })
                getDropdownEmployee()
                break;
            case TAB_KEY.WORKFLOW:
                getProjectWorkflow();
                break;
            default:
                break;
        }

    }, [getStpTimeSheetById, fetchDropdownTask, getDropdownEmployee, fetchDropdownRoleByProject, fetchtTaskPagination, fetchtEmployeeRoleMappingPagination, fetchCompanyDropdownRole, project])

    const onPageTaskChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];

            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination?.current - 1,
                    size: tablePagination?.pageSize || 10,
                    pageSize: tablePagination?.pageSize || 10,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            fetchtTaskPagination(modifiedParams);
        },
        [fetchtTaskPagination]
    );

    const onPageEmployeeByRoleChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];

            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination?.current - 1,
                    size: tablePagination?.pageSize || 10,
                    pageSize: tablePagination?.pageSize || 10,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            fetchtEmployeePaginationByRoleIdAndTimeSheetId(modifiedParams);
        },
        [fetchtEmployeePaginationByRoleIdAndTimeSheetId]
    );

    useEffect(() => {
        if (id && currentCompany) {
            getStpTimeSheetById(id)
        }
    }, [id, getStpTimeSheetById])

    const getProjectWorkflow = useCallback(
        async (values) => {
            try {
                dispatch(setIsLoading(true));
                let res = await service.getProjectWorkflowList({
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    requestId: generateRandomString(),
                    stpTimeSheetId: id

                });
                dispatch(setProjectWorkflow(res?.data?.content || []))
                // Alert({ message: "Success" });
            } catch (error) {
                Alert({ type: "error", resultObject: error });
                dispatch(setProjectWorkflow({}))
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch, currentCompany, currentEmployee, id],
    )

    useEffect(() => {
        setList(projectWorkflow?.flowConfig || [])
    }, [projectWorkflow])

    const PlusDropdown = useCallback(
        (index) => {
            return (
                <Flex vertical style={{ display: "flex", paddingTop: "1vw", alignSelf: "center" }}>
                    <Flex style={{ padding: "30px 0px 50px 0px" }}>
                        <RightOutlined style={{ color: "var(--button-color)" }} />
                    </Flex>
                    {!isView && (
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        onClick: () => {
                                            let temp = [...list]
                                            temp.splice((index == null ? 0 : index + 1), 0, { type: 'ALL_CHAIN_OF_COMMAND' });
                                            setList([...temp.map((o, step) => {
                                                return {
                                                    ...o,
                                                }
                                            }).map((o, step) => ({ ...o, step: step + 1 }))])
                                        },
                                        key: "ALL_CHAIN_OF_COMMAND",
                                        label: (<span
                                        >
                                            {t("workflow.label.chain_of_command")}
                                        </span>),
                                    },
                                    {
                                        onClick: () => {
                                            let temp = [...list]
                                            temp.splice((index == null ? 0 : index + 1), 0, { type: 'userlist' });
                                            setList([...temp.map((o, step) => {
                                                return {
                                                    ...o,
                                                }
                                            }).map((o, step) => ({ ...o, step: step + 1 }))])
                                        },
                                        key: "userlist",
                                        label: (
                                            <span >
                                                {t("workflow.label.individual")}
                                            </span>
                                        ),
                                    },
                                ]
                            }}>
                            <PlusCircleOutlined style={{ color: "var(--button-color)" }} />
                        </Dropdown>)}
                </Flex>
            )
        },
        [list, setList],
    )

    const requestModel = useCallback(
        () => {
            return <>
                <Flex style={{ alignItems: "center" }} vertical>
                    <Card
                        styles={{
                            body: {
                                alignContent: "center",
                                padding: "16px 16px 10px 16px",
                                height: "100%"
                            }
                        }}
                        style={{
                            placeContent: "center",
                            display: "flex",
                            alignSelf: "center",
                            width: "16vw",
                            borderStyle: "solid",
                            borderColor: "transparent",
                            borderRadius: "0.5rem",
                            borderWidth: "2px",
                            textAlign: "center",
                            alignContent: "center"
                        }}>
                        <Flex
                            style={{
                                display: "flex",
                                placeContent: "center",
                                padding: "8px 16px",
                                textAlign: "center",
                                borderRadius: "8px",
                                border: "2px solid var(--button-color)",
                                color: "var(--button-color)",
                            }}>{t("workflow.label.requester")}
                        </Flex>

                    </Card>
                    <Card
                        styles={{
                            body: {
                                padding: "15px"
                            }
                        }}
                        style={{
                            width: "16vw",
                            borderStyle: "solid",
                            borderColor: "var(--button-color)",
                            borderRadius: "0.5rem",
                            borderWidth: "2px",
                        }}>
                        <Flex>
                            <Divider style={{
                                height: "3vw",
                                width: "0.1vw",
                                backgroundColor: "var(--button-color)",
                                borderBlockStart: "none",
                                borderRadius: "1vw"
                            }} type="vertical" />
                            <Flex vertical>
                                <span style={{ fontSize: "1vw", color: "var(--button-color)" }}>{i18n.language == "en" ? "Everyone" : "ทุกคน"}</span>
                                <span style={{ fontSize: "1vw", color: "#AEAEAE" }}>{i18n.language == "en" ? "in project" : "ในโปรเจ็ค"}</span>
                            </Flex>
                        </Flex>
                    </Card>
                </Flex>
                <Flex vertical style={{ display: "flex", alignSelf: "center" }}>
                    {PlusDropdown(0)}
                </Flex>
            </>
        },
        [list, setList, PlusDropdown],
    )

    const selectedText = (selectedValue) => {
        switch (selectedValue) {
            // case 'ALL_CHAIN_OF_COMMAND':
            //     return (
            //         <Flex vertical>
            //             <Flex style={{ fontSize: "1vw", width: "11vw" }}>
            //                 {i18n.language == "en" ? "Approve All" : "อนุมัติทุกลำดับ"}
            //             </Flex>
            //             <Flex style={{ fontSize: "1vw", color: "#AEAEAE" }}>
            //                 {i18n.language == "en" ? "Must be approved by everyone in the chain of command." : "ต้องได้รับการอนุมัติจากทุกคนตามลำดับในสายบังคับบัญชา"}
            //             </Flex>
            //         </Flex>
            //     );
            case 'SUPERVISOR':
                return (
                    <Flex vertical>
                        <Flex style={{ fontSize: "1vw", width: "11vw" }}>
                            {i18n.language == "en" ? "Only direct supervisor" : "เฉพาะหัวหน้าโดยตรง"}
                        </Flex>
                        <Flex style={{ fontSize: "1vw", color: "#AEAEAE" }}>
                            {i18n.language == "en" ? "Must be approved by direct supervisor only." : "ต้องได้รับการอนุมัติจากหัวหน้าโดยตรงเท่านั้น"}
                        </Flex>
                    </Flex>
                );
            case 'OWNER':
                return (
                    <Flex vertical>
                        <Flex style={{ fontSize: "1vw", width: "11vw" }}>
                            {i18n.language == "en" ? "Only the responsible person" : "เฉพาะผู้รับผิดชอบ"}
                        </Flex>
                        <Flex style={{ fontSize: "1vw", color: "#AEAEAE" }}>
                            {i18n.language == "en" ? "Must be approved by responsible person only." : "ต้องได้รับการอนุมัติจากผู้รับผิดชอบเท่านั้น"}
                        </Flex>
                    </Flex>
                );
            case 'USERLIST':
                return (
                    <Flex vertical>
                        <Flex style={{ fontSize: "1vw", width: "11vw" }}>
                            {i18n.language == "en" ? "Person" : "บุคคล"}
                        </Flex>
                        {/* <Flex style={{ fontSize: "1vw", color: "#AEAEAE" }}>
                            {i18n.language == "en" ? "Must be approved by responsible person only." : "ต้องได้รับการอนุมัติจากผู้รับผิดชอบเท่านั้น"}
                        </Flex> */}
                    </Flex>
                );
            case 'no_require':
                return (
                    <Flex style={{ fontSize: "1vw", width: "11vw" }}>
                        {i18n.language == "en" ? "No approval required" : "ไม่ต้องอนุมัติ"}
                    </Flex>
                );
            default:
                return null;
        }
    };

    function ChainOfCommand(props) {
        const { param } = props;
        const [edit, setEdit] = useState(param.edit || false)
        return (
            <Flex vertical>{param.workFlowConfigType == "REQUESTER" ?
                <Card
                    styles={{
                        body: {
                            alignContent: "center",
                            padding: "16px 16px 10px 16px",
                            height: "100%"
                        }
                    }}
                    style={{
                        placeContent: "center",
                        alignSelf: "center",
                        display: "flex",
                        width: "16vw",
                        borderStyle: "solid",
                        borderColor: "transparent",
                        borderRadius: "0.5rem",
                        borderWidth: "2px",
                        textAlign: "center",
                        alignContent: "center"
                    }}>
                    <Flex
                        style={{
                            display: "flex",
                            placeContent: "center",
                            backgroundColor: "#D4EBFF",
                            padding: "0.4vw",
                            textAlign: "center",
                            borderStyle: "solid",
                            borderColor: "#1890FF",
                            borderRadius: "2rem",
                            borderWidth: "2px",
                            color: "#1890FF",
                            fontSize: "0.8vw",
                            width: "7vw"
                        }}>{t("workflow.label.requester")}
                    </Flex>
                </Card>
                : <Card
                    styles={{
                        body: {
                            padding: "15px"
                        }
                    }}
                    style={{
                        width: "16vw",
                        alignSelf: "center",
                        borderStyle: "solid",
                        borderColor: "transparent",
                        borderRadius: "0.5rem",
                        borderWidth: "2px",
                    }}>
                    <Flex style={{ justifyContent: "center" }}>
                        <Flex style={{
                            backgroundColor: "var(--button-color)",
                            justifyContent: "center",
                            borderRadius: "100%"
                        }}>
                            <span style={{ alignSelf: "center", color: "#FFFF", padding: "8px 15px" }}>{param.step - 1}</span>
                        </Flex>
                    </Flex>
                </Card>
            }
                <Card
                    styles={{
                        body: {
                            padding: "15px"
                        }
                    }}
                    style={{
                        width: "100%",
                        border: "2px solid var(--button-color)",
                        borderRadius: "0.5rem",
                    }}>

                    <Flex>
                        <Divider style={{
                            height: "9vw",
                            width: "0.2vw",
                            backgroundColor: "var(--button-color)",
                            borderBlockStart: "none",
                            borderRadius: "1vw"
                        }} type="vertical" />
                        <Flex style={{
                            minWidth: "12.5vw",
                            maxWidth: "12.5vw",
                        }} vertical>
                            <Flex style={{ fontSize: "1vw", width: "11vw", color: "var(--button-color)" }}>
                                {i18n.language == "en" ? "According to the chain of command" : "ตามสายบังคับบัญชา"}
                            </Flex>
                            <span style={{ fontSize: "1vw", color: "#AEAEAE" }}>
                                {i18n.language == "en" ? "Approval respectively" : "อนุมัติตามลำดับขั้น"}
                            </span>
                            <Divider
                                style={{
                                    height: "0.1vw",
                                    width: "0.1vw",
                                    backgroundColor: "var(--button-color)",
                                    borderBlockStart: "none",
                                    borderRadius: "1vw",
                                    margin: "10px 0px"
                                }} />
                            {edit ? (
                                <Select
                                    name="chainOfCommand"
                                    placeholder={i18n.language == "en" ? "According to the chain of command" : "ตามสายบังคับบัญชา"}
                                    size="large"
                                    value={param?.condition}
                                    onChange={(e) => {
                                        setList(list.map(o => {
                                            if (o.step == param.step) {
                                                return {
                                                    ...o,
                                                    condition: e,
                                                    isEdit: true
                                                }
                                            }
                                            else {
                                                return {
                                                    ...o
                                                }
                                            }
                                        }))
                                    }}
                                    options={[
                                        // { value: 'ALL_CHAIN_OF_COMMAND', label: <span>{i18n.language == "en" ? "Approve All" : "อนุมัติทุกลำดับ"} </span> },
                                        { value: 'SUPERVISOR', label: <span>{i18n.language == "en" ? "Only direct supervisor" : "เฉพาะหัวหน้าโดยตรง"}</span> },
                                        { value: 'OWNER', label: <span>{i18n.language == "en" ? "Only the responsible person" : "เฉพาะผู้รับผิดชอบ"}</span> },
                                        // { value: 'USERLIST', label: <span>{i18n.language == "en" ? "Person" : "บุคคล"}</span> },
                                        // { value: 'no_require', label: <span>{i18n.language == "en" ? "No approval required" : "ไม่ต้องอนุมัติ"}</span> },
                                    ]}
                                />
                            ) : (selectedText(param.condition))}
                        </Flex>
                        {!isView && (<span style={{ color: "#AEAEAE" }}>
                            <EditOutlined
                                style={{ paddingRight: "12px" }}
                                onClick={() => {
                                    setEdit(!edit)
                                    if (edit) {
                                        setList(list?.map(o => {
                                            if (o.step == param.step) {
                                                return {
                                                    ...o,
                                                    edit: false,
                                                }
                                            }
                                            else {
                                                return {
                                                    ...o
                                                }
                                            }
                                        }))
                                        setListWorkFlow(listworkflow?.map((o, index) => {
                                            if (index == param.other) {
                                                return {
                                                    ...o,
                                                    flowConfig: o.flowConfig.map((v1, step) => {
                                                        if (v1.step == param.step) {
                                                            return {
                                                                ...v1,
                                                                isEdit: true,
                                                                edit: false
                                                            }
                                                        }
                                                        return v1
                                                    })
                                                }
                                            }
                                            return o
                                        }))
                                    }
                                }} />

                            <DeleteOutlined onClick={() => {
                                handleDelete(() => {
                                    if (param.other == null) {
                                        setList(list.filter(o => o.step != param.step).map((o, step) => {
                                            return {
                                                ...o,
                                                step: step + 1,
                                                isEdit: true
                                            }
                                        }))
                                    } else {
                                        setList(list?.map(o => {
                                            return {
                                                ...o,
                                                isEdit: true,
                                            }
                                        }))
                                        if (param.step == 1) {
                                            setListWorkFlow(
                                                listworkflow.filter((o, index) => index != param.other).map((o, step) => {
                                                    return {
                                                        ...o,
                                                        step: step + 1,
                                                        isEdit: true,
                                                        edit: true
                                                    }
                                                })
                                            )
                                        } else {
                                            setListWorkFlow(listworkflow?.map((o, index) => {
                                                if (index == param.other) {
                                                    return {
                                                        ...o,
                                                        flowConfig: o?.flowConfig?.filter((v1, step) => v1.step != param.step)
                                                    }
                                                }
                                                return o
                                            }))
                                        }
                                    }
                                })

                            }} />
                        </span>)}
                    </Flex >
                </Card >
            </Flex>
        )
    }

    const handleDelete = useCallback(
        (fn = () => { }) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: (
                    <>
                        <p>
                            {t('workflow.are-you-sure-to-delete-this-item')}

                        </p>
                    </>
                ),
                onOk() {
                    if (fn) {
                        fn()
                    }
                },
                okText: <span>{t("common.confirm")}</span>,
                cancelText: <span>{t("common.cancel")}</span>,
            });
        }, []
    );

    function ApprovebyPerson(props) {
        const { param } = props;
        const [edit, setEdit] = useState(param.edit || false)
        return (
            <Flex vertical>{
                param.workFlowConfigType == "REQUESTER" ? <Card
                    styles={{
                        body: {
                            alignContent: "center",
                            padding: "16px 16px 10px 16px",

                            height: "100%"
                        }
                    }}
                    style={{
                        alignSelf: "center",
                        placeContent: "center",
                        display: "flex",
                        width: "16vw",
                        borderStyle: "solid",
                        borderColor: "transparent",
                        borderRadius: "0.5rem",
                        borderWidth: "2px",
                        textAlign: "center",
                        alignContent: "center",

                    }}>
                    <Flex
                        style={{
                            display: "flex",
                            placeContent: "center",
                            backgroundColor: "#D4EBFF",
                            padding: "0.4vw",
                            textAlign: "center",
                            borderStyle: "solid",
                            borderColor: "#1890FF",
                            borderRadius: "2rem",
                            borderWidth: "2px",
                            color: "#1890FF",
                            fontSize: "0.8vw",
                            width: "7vw"
                        }}>{t("workflow.label.requester")}
                    </Flex>
                </Card>
                    : <Card
                        styles={{
                            body: {
                                padding: "15px",
                            }
                        }}
                        style={{
                            width: "16vw",
                            alignSelf: "center",
                            borderStyle: "solid",
                            borderColor: "transparent",
                            borderRadius: "0.5rem",
                            borderWidth: "2px",
                        }}>
                        <Flex style={{ justifyContent: "center", }}>
                            <Flex style={{
                                backgroundColor: "var(--button-color)",
                                justifyContent: "center",
                                borderRadius: "100%"
                            }}>
                                <span style={{ alignSelf: "center", color: "#FFFF", padding: "8px 15px" }}>{param.step - 1}</span>
                            </Flex>
                        </Flex>
                    </Card>
            }
                <Card
                    styles={{
                        body: {
                            padding: "15px"
                        }
                    }}
                    style={{
                        width: "100%",
                        border: "2px solid var(--button-color)",
                        borderRadius: "0.5rem",
                    }}>

                    <Flex>
                        <Divider style={{
                            height: "9vw",
                            width: "0.2vw",
                            backgroundColor: "var(--button-color)",
                            borderBlockStart: "none",
                            borderRadius: "1vw"
                        }} type="vertical" />
                        <Flex style={{
                            minWidth: "12.5vw",
                            maxWidth: "12.5vw",
                        }} vertical>
                            <Flex style={{ fontSize: "1vw", width: "11vw", color: "var(--button-color)" }}>
                                {t("workflow.label.approve_by_individual")}
                            </Flex>
                            <Divider
                                style={{
                                    height: "0.1vw",
                                    width: "0.1vw",
                                    backgroundColor: "var(--button-color)",
                                    borderBlockStart: "none",
                                    borderRadius: "1vw",
                                    margin: "10px 0px"
                                }} />
                            <Flex vertical style={{
                                rowGap: "8px"
                            }}>
                                {edit ? (
                                    <Select
                                        name="person"
                                        placeholder={t("workflow.label.individual")}
                                        size="large"
                                        mode={'multiple'}
                                        value={param?.requesterList?.map?.(o => o.employeeId) || []}
                                        options={employeeOptions}
                                        filterOption={(input, option) => {
                                            return (option?.fullName + option.fullNameEn ?? "")
                                                .toLowerCase()
                                                .includes(input.toLowerCase());
                                        }}
                                        onChange={(e, option) => {
                                            if (param.other == null) {
                                                setList(list?.map(o => {
                                                    if (o.step == param.step) {
                                                        return {
                                                            ...o,
                                                            edit: true,
                                                            isEdit: true,
                                                            requesterList: option.map(({ value, name, surName }) => {
                                                                return {
                                                                    employeeId: value,
                                                                    name: name,
                                                                    surname: surName
                                                                }
                                                            },
                                                            )
                                                        }
                                                    }
                                                    else {
                                                        return {
                                                            ...o
                                                        }
                                                    }
                                                }))
                                            } else {
                                                setList(list?.map(o => {
                                                    return {
                                                        ...o,
                                                        isEdit: true,
                                                    }
                                                }))
                                                setListWorkFlow(listworkflow?.map((o, index) => {
                                                    if (index == param.other) {
                                                        return {
                                                            ...o,
                                                            flowConfig: o.flowConfig.map((v1, step) => {
                                                                if (v1.step == param.step) {
                                                                    return {
                                                                        ...v1,
                                                                        requesterList: option.map(({ value, name, surName }) => {
                                                                            return {
                                                                                employeeId: value,
                                                                                name: name,
                                                                                surname: surName
                                                                            }
                                                                        },
                                                                        ),
                                                                        isEdit: true,
                                                                        edit: true
                                                                    }
                                                                }
                                                                return v1
                                                            })
                                                        }
                                                    }
                                                    return o
                                                }))
                                            }
                                        }}
                                    />
                                ) :
                                    (
                                        <Flex vertical style={{ fontSize: "1vw" }}>
                                            {param?.requesterList?.map?.(o => {
                                                return <span>{employeeOptions?.find?.((v) => o.employeeId == v.value)?.label}</span>
                                            })}
                                        </Flex>
                                    )}
                            </Flex>
                        </Flex>
                        {!isView && (<span style={{ color: "#AEAEAE" }}>
                            <EditOutlined style={{ paddingRight: "12px" }} onClick={() => {
                                setEdit(!edit)
                                if (edit) {
                                    if (param.other == null) {
                                        setList(list?.map(o => {
                                            if (o.step == param.step) {
                                                return {
                                                    ...o,
                                                    edit: false,
                                                }
                                            }
                                            else {
                                                return {
                                                    ...o
                                                }
                                            }
                                        }))
                                    }

                                }
                            }} />
                            <DeleteOutlined onClick={() => {
                                handleDelete(() => {
                                    if (param.other == null) {
                                        setList(list.filter(o => o.step != param.step).map((o, step) => {
                                            return {
                                                ...o,
                                                step: step + 1,
                                                isEdit: true
                                            }
                                        }))
                                    } else {
                                        setList(list?.map(o => {
                                            return {
                                                ...o,
                                                isEdit: true,
                                            }
                                        }))
                                        if (param.step == 1) {
                                            setListWorkFlow(
                                                listworkflow.filter((o, index) => index != param.other).map((o, step) => {
                                                    return {
                                                        ...o,
                                                        step: step + 1,
                                                        isEdit: true,
                                                        edit: true
                                                    }
                                                })
                                            )
                                        } else {
                                            setListWorkFlow(listworkflow?.map((o, index) => {
                                                if (index == param.other) {
                                                    return {
                                                        ...o,
                                                        flowConfig: o?.flowConfig?.filter((v1, step) => v1.step != param.step)
                                                    }
                                                }
                                                return o
                                            }))
                                        }
                                    }
                                })

                            }} />
                        </span>)}
                    </Flex>
                </Card>
            </Flex>
        )
    }

    const RenderCell = useCallback(
        () => {
            return list.map((o, index) => {
                function request(type) {
                    return <Flex gap={40} style={{ display: "flex" }}>
                        {requestModel()}
                    </Flex>;
                }
                function approveModel(type) {
                    switch (type) {
                        case 'ALL_CHAIN_OF_COMMAND':
                            return <Flex gap={40} style={{ display: "flex" }}>
                                <ChainOfCommand param={{ ...o, approve: 'Y' }} />
                                {PlusDropdown(index)}
                            </Flex>;
                        case 'userlist':
                            return <Flex gap={40} style={{ display: "flex" }}>
                                <ApprovebyPerson param={{ ...o, approve: 'Y' }} />
                                {PlusDropdown(index)}
                            </Flex>;
                        default: return null
                    }
                }

                if (o.workFlowConfigType == "REQUESTER") {
                    return request()
                } else {
                    return approveModel(o.type)
                }


            })
        },
        [list, requestModel],
    )

    const editWorkFlowFn = useCallback(
        async (values) => {
            try {
                dispatch(setIsLoading(true));
                let res = await service.editProjectWorkFlow({
                    ...values,
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    employeeId: currentEmployee,
                    stpTimeSheetId: id
                });
                getProjectWorkflow(projectWorkflow)
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
            }
        },
        [dispatch, currentCompany, currentEmployee, getProjectWorkflow, projectWorkflow],
    )

    const onSave = useCallback(
        async (prop) => {
            let flowConfig = [...list?.map?.((o, step) => {
                return {
                    ...o,
                    role: o?.role?.roleId,
                    position: o?.position?.positionId,
                    requesterList: o?.requesterList?.map?.(o => o.employeeId),
                    step: step + 1
                }
            })]

            let req = {
                ...projectWorkflow,
                flowConfig,
            }
            await editWorkFlowFn(req)
        },
        [list, projectWorkflow, editWorkFlowFn],
    )

    const onCancelWorkflow = useCallback(
        () => {
            getProjectWorkflow()
        },
        [getProjectWorkflow],
    )

    const [formUse] = useForm(null);

    const HandleSave = useCallback(
        (fn = () => { }) => {
            Modal.confirm({
                title: t("dialog.confirmation.header"),
                content: t("dialog.confirmation.edit_action"),
                onOk() {
                    if (onSave) {
                        onSave()
                    }
                },
                okText: <span>{t("common.confirm")}</span>,
                cancelText: <span>{t("common.cancel")}</span>,
            })

        },
        [onSave, formUse]
    );

    const deleteRole = useCallback(async (params) => {
        dispatch(setIsLoading(true))
        try {
            const response = await service.deleteTaskAndRole({
                companyId: currentCompany,
                employeeId: currentEmployee,
                stpTimeSheetId: id,
                roleId: params?.role?.roleId
            })
        } catch (error) {
            Alert({ type: "error", resultObject: error })
        } finally {
            fetchtTaskPagination()
            dispatch(setIsLoading(false))

        }

    }, [dispatch, currentCompany, currentEmployee, fetchtTaskPagination])

    const onDeleteRole = (e) => {
        Modal.confirm({
            title: t('dialog.confirmation.header'),
            content: (
                <>
                    <p>{t('dialog.confirmation.delete')}</p>
                </>
            ),
            onOk() {
                deleteRole(e)
            },
            okText: t('confirm-2'),
            cancelText: t('masterData.cancel'),
        });
    };

    const deleteTask = useCallback(async (params) => {
        dispatch(setIsLoading(true))
        try {
            const response = await service.deleteTaskById({
                companyId: currentCompany,
                employeeId: currentEmployee,
                stpTimeSheetId: id,
                roleId: params?.role?.roleId,
                taskId: params?.id
            })
        } catch (error) {
            Alert({ type: "error", resultObject: error })
        } finally {
            fetchtTaskPagination()
            dispatch(setIsLoading(false))

        }

    }, [dispatch, currentCompany, currentEmployee, fetchtTaskPagination])

    const onDeleteTask = (e) => {
        Modal.confirm({
            title: t('dialog.confirmation.header'),
            content: (
                <>
                    <p>{t('dialog.confirmation.delete')}</p>
                </>
            ),
            onOk() {
                deleteTask(e)
            },
            okText: t('confirm-2'),
            cancelText: t('masterData.cancel'),
        });
    };

    return {
        isModalOpen,
        isRoleModalOpen,
        showModal,
        onCancel,
        showRoleModal,
        onRoleModalCancel,
        project,
        onSubmit,
        PROJECT_STATUS,
        getStpTimeSheetById,
        id,
        onTabChange,
        onSubmitTask,
        taskOption,
        taskPagination,
        onEditTask,
        taskOnEdit,
        paginationTask,
        onPageTaskChange,
        roleOption,
        employeeOptions,
        onSubmitMappingEmployeeRole,
        onAddEmployee,
        tableDataSourceCreate,
        employeeRoleMappingPagination,
        paginationEmployeeMapping,
        onViewRole,
        onEditRole,
        isRoleEditModalOpen,
        onRoleEditModalCancel,
        roleModalData: roleOnEdit,
        employeePaginationByRole,
        action: roleAction,
        onPageEmployeeByRoleChange,
        paginationEmployeeByRole,
        setSearchTxt,
        onSearchEmployeeByRole,
        onRemoveEmployeeFromRole,
        RenderCell,
        HandleSave,
        onCancelWorkflow,
        list,
        selectedRoleId,
        onRoleChange,
        onDeleteRole,
        onDeleteTask,
        onRemoveEmployeeRoleCreate,
        isLoading
    }
}

export default useEditSettingProject;